import { useState, useEffect } from 'react';
import { Camera, X, Loader2, ImageIcon } from 'lucide-react';
import { serviceCenterRepairsApi } from '@/api/service-center-repairs';

interface CameraCaptureProps {
  existingPhoto?: string;
  onCapture: (blob: Blob | null) => void;
}

export function CameraCapture({ existingPhoto, onCapture }: CameraCaptureProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Fetch existing photo through authenticated API
  useEffect(() => {
    if (!existingPhoto || preview) {
      setExistingPhotoUrl(null);
      return;
    }

    let cancelled = false;

    async function fetchImage() {
      try {
        setLoading(true);
        setError(false);
        const url = await serviceCenterRepairsApi.getPhotoBlobUrl(existingPhoto!);
        if (!cancelled) {
          setExistingPhotoUrl(url);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchImage();

    return () => {
      cancelled = true;
      if (existingPhotoUrl) {
        URL.revokeObjectURL(existingPhotoUrl);
      }
    };
  }, [existingPhoto, preview]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreview(URL.createObjectURL(file));
      onCapture(file);
    }
  };

  const handleClear = () => {
    setPreview(null);
    onCapture(null);
  };

  const photoUrl = preview || existingPhotoUrl;

  return (
    <div className="space-y-2">
      {/* Loading state */}
      {loading && (
        <div className="w-full h-48 rounded border bg-muted flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error state */}
      {error && !loading && !photoUrl && (
        <div className="w-full h-32 rounded border bg-muted flex flex-col items-center justify-center gap-2">
          <ImageIcon className="w-6 h-6 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Не удалось загрузить фото</span>
        </div>
      )}

      {/* Photo display */}
      {photoUrl && !loading && (
        <div className="relative">
          <a href={photoUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={photoUrl}
              alt="Фото устройства"
              className="w-full max-h-48 object-contain rounded border hover:opacity-80 transition-opacity"
            />
          </a>
          {preview && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <label className="flex items-center justify-center gap-2 p-3 border border-dashed rounded-lg cursor-pointer hover:bg-accent transition-colors">
        <Camera className="w-5 h-5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {photoUrl ? 'Переснять фото' : 'Сделать фото'}
        </span>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleChange}
          className="hidden"
        />
      </label>
    </div>
  );
}
